import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";

const RATE_BASES = [
  { v: "hourly", l: "Hourly ($/hr)" },
  { v: "daily",  l: "Daily ($/day)" },
  { v: "m2",     l: "per m²" },
  { v: "m",      l: "per Linear metre" },
  { v: "each",   l: "per Item" },
  { v: "point",  l: "per Point" },
  { v: "fixed",  l: "Fixed amount" },
];
const RATE_SOURCES = [
  { v: "market_benchmark",         l: "Market Benchmark" },
  { v: "protected_recommendation", l: "Protected Recommendation" },
  { v: "luxe_active",              l: "Signature Active" },
  { v: "preferred_contractor",     l: "Preferred Contractor" },
];
const RECIPE_BASES = ["area_m2", "linear_m", "each", "point", "fixed"];
const CATEGORIES = ["structure", "media", "cabinetry", "finishes",
                     "electrical", "lighting", "services", "other"];

const fmt$ = (c) => `$${((c || 0) / 100).toLocaleString("en-AU", { minimumFractionDigits: 2 })}`;


export default function PricingPage() {
  const [tab, setTab] = useState("rates");
  return (
    <div className="px-6 lg:px-14 py-10 lg:py-14 max-w-7xl" data-testid="pricing-page">
      <div className="mb-10">
        <div className="overline">Studio · Trade Operations</div>
        <h1 className="font-serif font-light mt-3" style={{ fontSize: "44px", lineHeight: 1.05 }}>Pricing</h1>
        <p className="text-sm text-neutral-600 mt-3 max-w-2xl">
          Manage Trades, Rate Cards (Market Benchmark → Preferred Contractor), Pricing Recipes and defaults.
          Project-specific Quotes are managed inside each Project.
        </p>
      </div>
      <div className="flex gap-1 border-b border-neutral-200 mb-8">
        {[
          { k: "rates",    l: "Trades & Rates",   tid: "tab-trades-rates" },
          { k: "recipes",  l: "Pricing Recipes",  tid: "tab-recipes" },
          { k: "settings", l: "Pricing Settings", tid: "tab-pricing-settings" },
        ].map(({ k, l, tid }) => (
          <button key={k} data-testid={tid}
                    onClick={() => setTab(k)}
                    className={`px-4 py-2 text-sm border-b-2 -mb-px transition ${
                      tab === k
                        ? "border-neutral-900 text-neutral-900 font-medium"
                        : "border-transparent text-neutral-500 hover:text-neutral-800"
                    }`}>{l}</button>
        ))}
      </div>
      {tab === "rates"    && <TradesRates />}
      {tab === "recipes"  && <PricingRecipes />}
      {tab === "settings" && <PricingSettings />}
    </div>
  );
}


// ── Trades & Rate Cards ────────────────────────────────────────────
function TradesRates() {
  const [trades, setTrades]   = useState([]);
  const [cards, setCards]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTradeId, setSelectedTradeId] = useState(null);
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [showRateModal, setShowRateModal]   = useState(false);

  const reload = async () => {
    setLoading(true);
    const [tr, rc] = await Promise.all([api.get("/trades"), api.get("/rate-cards")]);
    setTrades(tr.data); setCards(rc.data); setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const cardsByTrade = useMemo(() => {
    const m = {};
    for (const c of cards) (m[c.trade_id] ||= []).push(c);
    return m;
  }, [cards]);

  const currentTrade = trades.find((t) => t.id === selectedTradeId);
  const currentCards = cardsByTrade[selectedTradeId] || [];

  return (
    <div className="grid gap-6 lg:grid-cols-[320px_1fr]" data-testid="trades-rates">
      <div className="bg-white border border-neutral-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-medium">Trades</h2>
          <button data-testid="btn-add-trade"
                    onClick={() => setShowTradeModal(true)}
                    className="text-xs px-3 py-1.5 border border-neutral-300 rounded-md hover:bg-neutral-50 inline-flex items-center gap-1">
            <Plus className="w-3 h-3"/>Trade
          </button>
        </div>
        {loading ? <Loader2 className="w-4 h-4 animate-spin"/> :
          trades.length === 0 ? (
            <p className="text-sm text-neutral-500">No trades yet. Add your first trade to begin.</p>
          ) : (
            <ul className="space-y-1" data-testid="trades-list">
              {trades.map((t) => {
                const rows = cardsByTrade[t.id] || [];
                const active = rows.filter((r) => r.status === "active");
                const top = active.sort((a, b) => sourceRank(b.source_type) - sourceRank(a.source_type))[0];
                return (
                  <li key={t.id}>
                    <button data-testid={`trade-row-${t.id}`}
                              onClick={() => setSelectedTradeId(t.id)}
                              className={`w-full text-left px-3 py-2 rounded-md text-sm transition ${
                                selectedTradeId === t.id ? "bg-neutral-900 text-white" : "hover:bg-neutral-50"
                              }`}>
                      <div className="font-medium">{t.name}</div>
                      <div className={`text-xs ${selectedTradeId === t.id ? "text-neutral-300" : "text-neutral-500"}`}>
                        {top ? `${top.source_type.replace(/_/g," ")} · ${fmt$(top.amount_cents)}/${top.rate_basis}` : "No rates"}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl p-6" data-testid="trade-detail">
        {!currentTrade ? (
          <p className="text-neutral-500 text-sm">Select a trade to see rate history.</p>
        ) : (
          <>
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-xl font-medium" data-testid="trade-detail-name">{currentTrade.name}</h2>
                <div className="text-xs text-neutral-500 mt-1">
                  {currentTrade.category || "General"} · {currentCards.length} rate {currentCards.length === 1 ? "card" : "cards"}
                </div>
              </div>
              <button data-testid="btn-add-rate"
                        onClick={() => setShowRateModal(true)}
                        className="text-sm px-4 py-2 bg-neutral-900 text-white rounded-md hover:bg-neutral-800 inline-flex items-center gap-2">
                <Plus className="w-4 h-4"/>Add Rate
              </button>
            </div>
            {currentCards.length === 0 ? (
              <p className="text-neutral-500 text-sm">No rate cards yet.</p>
            ) : (
              <div className="overflow-x-auto -mx-6">
                <table className="min-w-full text-sm" data-testid="rate-history">
                  <thead className="text-left text-xs uppercase text-neutral-500">
                    <tr className="border-b border-neutral-200">
                      <th className="px-6 py-2">Source</th>
                      <th className="px-6 py-2">Rate</th>
                      <th className="px-6 py-2">Basis</th>
                      <th className="px-6 py-2">Region</th>
                      <th className="px-6 py-2">Effective</th>
                      <th className="px-6 py-2">Contractor</th>
                      <th className="px-6 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentCards.map((r) => (
                      <tr key={r.id} className="border-b border-neutral-100"
                          data-testid={`rate-row-${r.id}`}>
                        <td className="px-6 py-3 capitalize">{r.source_type.replace(/_/g, " ")}</td>
                        <td className="px-6 py-3 font-medium">{fmt$(r.amount_cents)}</td>
                        <td className="px-6 py-3">{r.rate_basis}</td>
                        <td className="px-6 py-3">{r.region || "—"}</td>
                        <td className="px-6 py-3 text-neutral-500">
                          {r.effective_date ? r.effective_date.slice(0, 10) : "—"}
                        </td>
                        <td className="px-6 py-3 text-neutral-500">{r.contractor_name || "—"}</td>
                        <td className="px-6 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            r.status === "active"
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-neutral-100 text-neutral-500"
                          }`}>{r.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {showTradeModal && <TradeModal onClose={() => setShowTradeModal(false)} onSaved={reload}/>}
      {showRateModal  && currentTrade && (
        <RateModal tradeId={currentTrade.id} onClose={() => setShowRateModal(false)} onSaved={reload}/>
      )}
    </div>
  );
}

const RANK = { market_benchmark: 1, protected_recommendation: 2, luxe_active: 3, preferred_contractor: 4 };
const sourceRank = (s) => RANK[s] || 0;

function TradeModal({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const save = async () => {
    if (!name.trim()) return toast.error("Name required");
    try {
      await api.post("/trades", { name: name.trim(), category: category || undefined });
      toast.success("Trade added"); onSaved(); onClose();
    } catch { toast.error("Failed to save"); }
  };
  return (
    <Modal title="Add Trade" onClose={onClose} testid="modal-add-trade">
      <label className="block mb-3">
        <span className="text-sm text-neutral-700">Trade name</span>
        <input data-testid="input-trade-name" className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"
                value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Electrician"/>
      </label>
      <label className="block mb-6">
        <span className="text-sm text-neutral-700">Category</span>
        <select className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"
                  value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">—</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </label>
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 text-sm">Cancel</button>
        <button data-testid="btn-save-trade" onClick={save}
                  className="px-4 py-2 bg-neutral-900 text-white rounded-md text-sm hover:bg-neutral-800">Save</button>
      </div>
    </Modal>
  );
}

function RateModal({ tradeId, onClose, onSaved }) {
  const [f, setF] = useState({
    source_type: "luxe_active", rate_basis: "hourly", amount: "",
    region: "sunshine_coast", effective_date: new Date().toISOString().slice(0, 10),
    contractor_name: "", quote_reference: "", expiry_date: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    if (!f.amount || Number(f.amount) <= 0) return toast.error("Amount required");
    setSaving(true);
    try {
      await api.post("/rate-cards", {
        trade_id: tradeId,
        source_type: f.source_type,
        rate_basis: f.rate_basis,
        amount_cents: Math.round(Number(f.amount) * 100),
        region: f.region || undefined,
        effective_date: f.effective_date || undefined,
        contractor_name: f.contractor_name || undefined,
        quote_reference: f.quote_reference || undefined,
        expiry_date: f.expiry_date || undefined,
        notes: f.notes || undefined,
      });
      toast.success("Rate added"); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Add Rate Card" onClose={onClose} testid="modal-add-rate">
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Field label="Source">
          <select data-testid="input-rate-source"
                    value={f.source_type} onChange={(e) => set("source_type", e.target.value)}
                    className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm">
            {RATE_SOURCES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
        </Field>
        <Field label="Rate basis">
          <select data-testid="input-rate-basis"
                    value={f.rate_basis} onChange={(e) => set("rate_basis", e.target.value)}
                    className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm">
            {RATE_BASES.map((b) => <option key={b.v} value={b.v}>{b.l}</option>)}
          </select>
        </Field>
        <Field label="Amount ($)">
          <input data-testid="input-rate-amount" type="number" step="0.01"
                    value={f.amount} onChange={(e) => set("amount", e.target.value)}
                    className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/>
        </Field>
        <Field label="Region">
          <input value={f.region} onChange={(e) => set("region", e.target.value)}
                    className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/>
        </Field>
        <Field label="Effective date">
          <input type="date" value={f.effective_date} onChange={(e) => set("effective_date", e.target.value)}
                    className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/>
        </Field>
        <Field label="Expiry date (optional)">
          <input type="date" value={f.expiry_date} onChange={(e) => set("expiry_date", e.target.value)}
                    className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/>
        </Field>
        {f.source_type === "preferred_contractor" && (
          <>
            <Field label="Contractor name">
              <input data-testid="input-contractor-name" value={f.contractor_name}
                        onChange={(e) => set("contractor_name", e.target.value)}
                        className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/>
            </Field>
            <Field label="Agreement reference">
              <input value={f.quote_reference} onChange={(e) => set("quote_reference", e.target.value)}
                        className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/>
            </Field>
          </>
        )}
      </div>
      <Field label="Notes">
        <textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)}
                    className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/>
      </Field>
      <div className="flex justify-end gap-2 mt-6">
        <button onClick={onClose} className="px-4 py-2 text-sm">Cancel</button>
        <button data-testid="btn-save-rate" disabled={saving} onClick={save}
                  className="px-4 py-2 bg-neutral-900 text-white rounded-md text-sm hover:bg-neutral-800 disabled:opacity-50">
          {saving ? "Saving…" : "Save Rate"}
        </button>
      </div>
    </Modal>
  );
}


// ── Pricing Recipes ────────────────────────────────────────────────
function PricingRecipes() {
  const [recipes, setRecipes] = useState([]);
  const [trades, setTrades]   = useState([]);
  const [show, setShow]       = useState(false);
  const [q, setQ]             = useState("");
  const reload = async () => {
    const [r, t] = await Promise.all([api.get("/pricing-recipes"), api.get("/trades")]);
    setRecipes(r.data); setTrades(t.data);
  };
  useEffect(() => { reload(); }, []);
  const rows = recipes.filter((r) =>
    !q || r.name.toLowerCase().includes(q.toLowerCase()) ||
    (r.applies_to_type || "").toLowerCase().includes(q.toLowerCase())
  );
  return (
    <div data-testid="pricing-recipes">
      <div className="flex items-center justify-between mb-6">
        <input placeholder="Filter recipes…"
               aria-label="Filter recipes"
               value={q}
               onChange={(e) => setQ(e.target.value)}
               data-testid="input-search-recipes"
               className="border border-neutral-300 rounded-md px-3 py-2 text-sm w-64"/>
        <button data-testid="btn-add-recipe" onClick={() => setShow(true)}
                  className="text-sm px-4 py-2 bg-neutral-900 text-white rounded-md hover:bg-neutral-800 inline-flex items-center gap-2">
          <Plus className="w-4 h-4"/>Add Recipe
        </button>
      </div>
      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
            <tr>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Applies to</th>
              <th className="px-4 py-3 text-left">Basis</th>
              <th className="px-4 py-3 text-left">Trade</th>
              <th className="px-4 py-3 text-left">Waste</th>
              <th className="px-4 py-3 text-left">Version</th>
              <th className="px-4 py-3 text-left">Active</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-neutral-500">
                No recipes yet.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-neutral-100" data-testid={`recipe-row-${r.id}`}>
                <td className="px-4 py-3 font-medium">{r.name}</td>
                <td className="px-4 py-3 text-neutral-600">{r.applies_to_type}</td>
                <td className="px-4 py-3">{r.quantity_basis}</td>
                <td className="px-4 py-3 text-neutral-600">
                  {(trades.find((t) => t.id === r.trade_id) || {}).name || "—"}
                </td>
                <td className="px-4 py-3">{r.waste_pct || 0}%</td>
                <td className="px-4 py-3">v{r.version || 1}</td>
                <td className="px-4 py-3">{r.active !== false ? "Yes" : "No"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {show && <RecipeModal trades={trades} onClose={() => setShow(false)} onSaved={reload}/>}
    </div>
  );
}

function RecipeModal({ trades, onClose, onSaved }) {
  const [f, setF] = useState({
    name: "", applies_to_type: "", category: "electrical",
    trade_id: "", quantity_basis: "each", labour_rate_basis: "hourly",
    productivity_hr_per_unit: "", waste_pct: 0, fixings_pct: "",
    minimum_charge_cents: 0, material_rate_cents_per_unit: "",
  });
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    if (!f.name || !f.applies_to_type) return toast.error("Name + Applies to required");
    try {
      const body = {
        ...f,
        productivity_hr_per_unit: f.productivity_hr_per_unit ? Number(f.productivity_hr_per_unit) : null,
        waste_pct: Number(f.waste_pct || 0),
        fixings_pct: f.fixings_pct ? Number(f.fixings_pct) : null,
        minimum_charge_cents: Number(f.minimum_charge_cents || 0),
        material_rate_cents_per_unit: f.material_rate_cents_per_unit ? Math.round(Number(f.material_rate_cents_per_unit) * 100) : null,
      };
      await api.post("/pricing-recipes", body);
      toast.success("Recipe saved"); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal title="Add Pricing Recipe" onClose={onClose} testid="modal-add-recipe" wide>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Recipe name"><input data-testid="input-recipe-name" value={f.name}
          onChange={(e) => set("name", e.target.value)}
          className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/></Field>
        <Field label="Applies to (component type)">
          <input data-testid="input-recipe-applies-to" value={f.applies_to_type}
            onChange={(e) => set("applies_to_type", e.target.value)}
            placeholder="e.g. gpo_double, stone_panel, led_strip"
            className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/>
        </Field>
        <Field label="Category">
          <select value={f.category} onChange={(e) => set("category", e.target.value)}
                    className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Trade">
          <select data-testid="input-recipe-trade" value={f.trade_id}
                    onChange={(e) => set("trade_id", e.target.value)}
                    className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm">
            <option value="">—</option>
            {trades.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
        <Field label="Quantity basis">
          <select data-testid="input-recipe-basis" value={f.quantity_basis}
                    onChange={(e) => set("quantity_basis", e.target.value)}
                    className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm">
            {RECIPE_BASES.map((b) => <option key={b}>{b}</option>)}
          </select>
        </Field>
        <Field label="Labour rate basis">
          <select value={f.labour_rate_basis} onChange={(e) => set("labour_rate_basis", e.target.value)}
                    className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm">
            {RATE_BASES.map((b) => <option key={b.v} value={b.v}>{b.l}</option>)}
          </select>
        </Field>
        <Field label="Productivity (hr per unit)">
          <input type="number" step="0.01" value={f.productivity_hr_per_unit}
            onChange={(e) => set("productivity_hr_per_unit", e.target.value)}
            className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/>
        </Field>
        <Field label="Waste %">
          <input type="number" step="0.1" value={f.waste_pct}
            onChange={(e) => set("waste_pct", e.target.value)}
            className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/>
        </Field>
        <Field label="Fixings % (override)">
          <input type="number" step="0.1" value={f.fixings_pct}
            onChange={(e) => set("fixings_pct", e.target.value)}
            placeholder="Leave blank for project/global default"
            className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/>
        </Field>
        <Field label="Minimum charge ($)">
          <input type="number" step="0.01"
            value={f.minimum_charge_cents ? f.minimum_charge_cents / 100 : ""}
            onChange={(e) => set("minimum_charge_cents", Math.round(Number(e.target.value) * 100) || 0)}
            className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/>
        </Field>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button onClick={onClose} className="px-4 py-2 text-sm">Cancel</button>
        <button data-testid="btn-save-recipe" onClick={save}
                  className="px-4 py-2 bg-neutral-900 text-white rounded-md text-sm hover:bg-neutral-800">
          Save Recipe
        </button>
      </div>
    </Modal>
  );
}


// ── Pricing Settings ────────────────────────────────────────────────
function PricingSettings() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/pricing").then((r) => setF(r.data)); }, []);
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/pricing", f);
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  if (!f) return <Loader2 className="w-4 h-4 animate-spin"/>;
  return (
    <div data-testid="pricing-settings" className="bg-white border border-neutral-200 rounded-xl p-6 max-w-2xl">
      <h2 className="text-base font-medium mb-4">Studio Defaults</h2>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Region"><input data-testid="input-region" value={f.region || ""}
          onChange={(e) => set("region", e.target.value)}
          className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/></Field>
        <Field label="Trade Market Protection %"><input data-testid="input-protection-pct" type="number" step="0.1"
          value={f.trade_protection_pct} onChange={(e) => set("trade_protection_pct", Number(e.target.value))}
          className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/></Field>
        <Field label="Global fixings %"><input type="number" step="0.1" value={f.global_fixings_pct}
          onChange={(e) => set("global_fixings_pct", Number(e.target.value))}
          className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/></Field>
        <Field label="Default waste %"><input type="number" step="0.1" value={f.default_waste_pct}
          onChange={(e) => set("default_waste_pct", Number(e.target.value))}
          className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/></Field>
        <Field label="Sell rule">
          <select data-testid="input-sell-rule" value={f.sell_rule}
                    onChange={(e) => set("sell_rule", e.target.value)}
                    className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm">
            <option value="markup">Markup (Sell = Cost × (1 + m))</option>
            <option value="gross_margin">Gross margin (Sell = Cost / (1 − g))</option>
          </select>
        </Field>
        <Field label="Markup / margin %"><input data-testid="input-markup-pct" type="number" step="0.1"
          value={f.markup_or_margin_pct}
          onChange={(e) => set("markup_or_margin_pct", Number(e.target.value))}
          className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/></Field>
        <Field label="GST %"><input data-testid="input-gst-pct" type="number" step="0.1"
          value={f.gst_pct} onChange={(e) => set("gst_pct", Number(e.target.value))}
          className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"/></Field>
      </div>
      <div className="mt-6 flex justify-end">
        <button data-testid="btn-save-pricing-settings" disabled={saving} onClick={save}
                  className="px-4 py-2 bg-neutral-900 text-white rounded-md text-sm hover:bg-neutral-800 disabled:opacity-50">
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>
    </div>
  );
}


// ── Reusable atoms ──────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-neutral-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Modal({ title, children, onClose, testid, wide }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4"
          data-testid={testid} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
            className={`bg-white rounded-xl p-6 ${wide ? "max-w-3xl" : "max-w-lg"} w-full max-h-[90vh] overflow-y-auto`}>
        <h2 className="text-lg font-medium mb-6">{title}</h2>
        {children}
      </div>
    </div>
  );
}
